import { useEffect, useState, useMemo } from 'react';
import orderService from '../../api/services/order.service';
import { useToast } from '../../admin/context/ToastContext';
import errorHandler from '../../api/utils/errorHandler';
import { normalizeImageUrl } from '../../utils/imageUtils';
import './MyOrders.css';

export default function MyOrders({ onOrderClick }) {
  const { showToast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageDisplay, setPageDisplay] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const apiPage = useMemo(() => Math.max(0, pageDisplay - 1), [pageDisplay]);
  const queryLimit = useMemo(
    () => (String(limit).toLowerCase() === 'all' ? 100000 : Number(limit)),
    [limit]
  );

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await orderService.getUserOrders({ 
          page: apiPage, 
          limit: queryLimit
        });
        if (response.status || response.success) {
          const payload = response.data || [];
          const items = Array.isArray(payload) ? payload : (payload.data || []);
          setOrders(items);
          
          // Robust total count detection
          const metaTotal = response.count ?? response.total ?? payload.total ?? payload.count ?? response.meta?.total ?? response.data?.total ?? response.data?.count ?? null;
          
          // If we found a total from the backend, use it.
          // If not, we keep total as null to signal that we are in "stream/hasMore" mode
          if (metaTotal !== null) {
            setTotal(Number(metaTotal));
          } else if (pageDisplay === 1 && items.length < queryLimit) {
            // If we're on page 1 and got fewer items than requested, we know the actual total
            setTotal(items.length);
          } else if (pageDisplay === 1 && items.length === queryLimit) {
            // If we got exactly the limit on page 1 but no total count, reset total to null
            // to ensure the hasMore logic takes over
            setTotal(null);
          }
          
          setHasMore(items.length === queryLimit);
        } else {
          throw new Error(response.message || 'Failed to fetch orders');
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError('Failed to load your orders. Please try again later.');
        if (!errorHandler.isAuthError(err)) {
          showToast(err.message || 'Failed to load orders', 'error');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [apiPage, queryLimit, showToast]);

  const totalPages = (total !== null && total !== undefined) ? Math.ceil(total / queryLimit) : (hasMore ? pageDisplay + 1 : pageDisplay);
  const canPrev = pageDisplay > 1;
  const canNext = (total !== null && total !== undefined) ? pageDisplay < totalPages : hasMore;

  const handleLimitChange = (e) => {
    setLimit(e.target.value);
    setPageDisplay(1);
  };

  const handlePrevPage = () => {
    if (canPrev) setPageDisplay((prev) => prev - 1);
  };

  const handleNextPage = () => {
    if (canNext) setPageDisplay((prev) => prev + 1);
  };

  if (loading) {
    return <div className="my-orders-loading">Loading orders...</div>;
  }

  if (error) {
    return <div className="my-orders-error">{error}</div>;
  }

  if (orders.length === 0) {
    return <div className="my-orders-empty">You haven't placed any orders yet.</div>;
  }

  return (
    <div>
      {/* Pagination Controls */}
      <div className="my-orders-header">
        <div className="header-left">
          <h3>My Orders</h3>
          {total > 0 && (
            <span className="order-count">
              Total {total} order{total !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="header-right">
          <div className="limit-select">
            <label htmlFor="orders-limit">Show</label>
            <select
              id="orders-limit"
              className="limit-dropdown"
              value={String(limit)}
              onChange={handleLimitChange}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value="All">All</option>
            </select>
          </div>
        </div>
      </div>

      <div className="my-orders-container">
        {orders.map((order) => (
          <div key={order.uuid || order.id} className="order-card" onClick={() => onOrderClick(order)}>
            <div className="order-header">
              <div className="order-id-group">
                <span className="order-id">Order ID: <span className="mono-text">{order.order_number || order.order_uuid || order.uuid || order.id}</span></span>
                <span className="transaction-id">Transaction ID: <span className="mono-text">{order.transaction_uuid || 'N/A'}</span></span>
              </div>
              <span className="order-date">
                {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'Date N/A'}
              </span>
            </div>
            
            <div className="order-content">
              <div className="order-product-preview">
                <div className="order-img-container">
                  <img 
                    src={normalizeImageUrl(
                      (Array.isArray(order.details?.[0]?.product_image) ? order.details[0].product_image[0] : null) || 
                      order.details?.[0]?.product_image || 
                      order.details?.[0]?.image || 
                      order.details?.[0]?.image_url || 
                      order.details?.[0]?.product?.image || 
                      order.details?.[0]?.product?.product_image || 
                      order.details?.[0]?.product?.image_url
                    ) || '/placeholder-product.png'} 
                    alt={order.details?.[0]?.product_name || 'Product'} 
                    className="order-preview-img"
                    onError={(e) => { e.target.src = '/placeholder-product.png'; }}
                  />
                </div>
                <div className="order-preview-info">
                  <span className="order-preview-name">{order.details?.[0]?.product_name || 'Product'}</span>
                  {order.details?.length > 1 && (
                    <span className="order-preview-more">+{order.details.length - 1} more items</span>
                  )}
                </div>
              </div>
              
              <div className="order-footer">
                <div className="order-summary">
                  <span className="order-items-count">{(order.details || []).length} items</span>
                  <span className="order-total-amount">${Number(order.total_amount || 0).toFixed(2)}</span>
                </div>
                <div className="order-status">
                  <span className={`status-badge ${(order.order_status || 'pending').toLowerCase()}`}>
                    {(order.order_status || 'PENDING').toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
            
            {(order.order_status || '').toLowerCase() === 'cancelled' && (
              <div className="cancellation-notice">
                <div className="notice-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <div className="notice-content">
                  <p className="notice-title">Your order has been cancelled</p>
                  {order.reason && (
                    <div className="notice-reason">
                      <span className="notice-reason-label">Reason</span>
                      <span className="notice-reason-value">{order.reason}</span>
                    </div>
                  )}
                  <p className="notice-text">Don't worry, your refund will be processed within 2-3 business days. If you need any assistance, please contact our support team at <a href="mailto:Support@chilltreebotanicals.com" className="support-email">Support@chilltreebotanicals.com</a>.</p>
                </div>
              </div>
            )}
            
            <div className="order-click-hint">
              <span className="click-hint-text">Click to see complete detail of the order</span>
              <svg className="click-hint-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Footer */}
      {orders.length > 0 && (
        <div className="my-orders-footer">
          <div className="footer-left">
            {(() => {
              const totalCount = total ?? orders.length;
              const start = totalCount === 0 ? 0 : (pageDisplay - 1) * queryLimit + 1;
              const end = Math.min(pageDisplay * queryLimit, totalCount);
              return (
                <span className="footer-summary">
                  Showing {String(limit).toLowerCase() === 'all' ? 'All' : `${start}-${end}`} of {totalCount} order{totalCount !== 1 ? 's' : ''}
                </span>
              );
            })()}
          </div>
          <div className="footer-right pagination-controls">
            <button
              type="button"
              className="page-btn"
              onClick={handlePrevPage}
              disabled={!canPrev || loading}
            >
              Previous
            </button>
            <span className="page-indicator">
              Page {pageDisplay}{totalPages ? ` of ${totalPages}` : ''}
            </span>
            <button
              type="button"
              className="page-btn"
              onClick={handleNextPage}
              disabled={!canNext || loading}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
